import React, { useState } from 'react';
import { Plus, Power } from 'lucide-react';
import portalAdminsService from '../services/portalAdmins';
import usersService from '../services/users';
import { getToken } from '../utils/storage';
import useServerTable from '../hooks/useServerTable';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import PageHeader from '../components/common/PageHeader';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_OPTIONS, formatDate } from '../components/common/filters';
import Modal from '../components/common/Modal';
import { Button } from '../components/ui/Button';
import { NewUserForm } from '../components/NewUserForm';

const EMPTY_FORM = { username: '', first_name: '', last_name: '', gender: '', email_id: '', phone_no: '' };

// Username of the logged-in portal admin, read from the stored authZ JWT.
const getCurrentUsername = () => {
    try {
        // JWT segments are base64url: map to base64 before atob.
        const b64 = getToken().split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(b64)).username || '';
    } catch {
        return '';
    }
};

const PortalAdminsPage = () => {
    const notice = useNotice();
    const confirm = useConfirm();
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [tempPassword, setTempPassword] = useState('');
    const currentUsername = getCurrentUsername();

    // URL keys: status, created_from/created_to (one per column filter).
    const columns = [
        {
            key: 'username',
            header: 'Admin',
            sortKey: 'username',
            locked: true,
            render: (u) => (
                <div className="min-w-0">
                    <div className="font-medium text-slate-900">{u.username}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email_id}</div>
                </div>
            ),
        },
        {
            key: 'name',
            header: 'Name',
            sortKey: 'name',
            render: (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || '—',
        },
        { key: 'phone_no', header: 'Phone', render: (u) => u.phone_no || '—' },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (u) => <StatusBadge active={Boolean(u.is_active)} />,
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'created',
            header: 'Created',
            sortKey: 'created',
            defaultVisible: false,
            className: 'whitespace-nowrap text-slate-500',
            render: (u) => (u.created_on ? formatDate(u.created_on) : '—'),
            filter: { type: 'date' },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (u) =>
                // A portal admin can't disable their own account (the server returns 409).
                u.username !== currentUsername && (
                    <RowActions>
                        <RowAction
                            icon={Power}
                            label={u.is_active ? 'Disable portal admin' : 'Enable portal admin'}
                            tone={u.is_active ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(u)}
                        />
                    </RowActions>
                ),
        },
    ];

    // Disabled admins are listed too (so they can be re-enabled); filter by status to narrow.
    const table = useServerTable((query) => portalAdminsService.getPage(query), {
        defaultSort: { key: 'created', dir: 'desc' },
        columns,
    });

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setError('');
        setFormData(EMPTY_FORM);
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError('');
        const payload = {
            ...formData,
            username: formData.username.trim(),
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
        };
        if (!payload.username || !payload.first_name || !payload.last_name) {
            setError('Username, first name and last name cannot be blank.');
            return;
        }
        setSubmitting(true);
        try {
            const res = await portalAdminsService.create(payload);
            setShowCreateModal(false);
            setFormData(EMPTY_FORM);
            if (res?.data?.email_sent) {
                notice.success('Portal admin created. Credentials emailed.');
            } else {
                setTempPassword(res?.data?.temp_password || '');
                notice.success('Portal admin created, but the email could not be sent.');
            }
            table.reload();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create portal admin');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (u) => {
        const verb = u.is_active ? 'Disable' : 'Enable';
        const ok = await confirm({
            title: `${verb} portal admin ${u.username}?`,
            body: u.is_active
                ? 'They lose platform-level access until the account is enabled again.'
                : 'They regain full portal-admin access.',
            confirmLabel: verb,
            tone: u.is_active ? 'danger' : 'default',
        });
        if (!ok) return;
        try {
            await usersService.updateStatus(u.username, u.is_active ? 0 : 1);
            notice.success(`Portal admin ${u.username} ${u.is_active ? 'disabled' : 'enabled'}.`);
            table.reload();
        } catch (err) {
            notice.error(err, `Couldn’t ${u.is_active ? 'disable' : 'enable'} ${u.username}. Try again.`);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Portal admins"
                subtitle="Platform-level users not tied to any application. They have full access to User Management."
                actions={
                    <Button
                        onClick={() => {
                            setError('');
                            setShowCreateModal(true);
                        }}
                    >
                        <Plus className="h-4 w-4" /> Add Portal Admin
                    </Button>
                }
            />

            {tempPassword && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-2">
                    <p>
                        The email could not be sent. Share this one-time password securely. It is shown only once.
                    </p>
                    <div className="flex items-center gap-3">
                        <code className="rounded bg-white px-2 py-1 font-mono">{tempPassword}</code>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigator.clipboard?.writeText(tempPassword)}
                        >
                            Copy
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setTempPassword('')}>
                            Dismiss
                        </Button>
                    </div>
                </div>
            )}

            <DataTable
                {...table.tableProps}
                columns={columns}
                tableKey="portal-admins"
                rowKey="username"
                searchPlaceholder="Search username, name, email, phone…"
                emptyMessage="No portal admins found."
            />

            <Modal isOpen={showCreateModal} onClose={closeCreateModal} title="Add Portal Admin" size="lg">
                <form onSubmit={handleCreate} className="space-y-4">
                    <NewUserForm formData={formData} setFormData={setFormData} />
                    <p className="text-xs text-slate-500">
                        A temporary password is generated and emailed. This user gets full portal-admin access and no
                        app membership.
                    </p>
                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}
                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <Button type="submit" disabled={submitting} className="flex-1">
                            {submitting ? 'Creating...' : 'Create Portal Admin'}
                        </Button>
                        <Button type="button" variant="outline" onClick={closeCreateModal} className="flex-1">
                            Cancel
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default PortalAdminsPage;
